/**
 * MobilePostViewOverlay — Full-screen overlay for viewing a community post.
 * Route: /community/:communitySlug/:postSlug
 *
 * Replicates PostView.tsx community theming logic exactly:
 *  - Fetches community customization by slug
 *  - Applies HSV/TMI/RGB color transformations when colorSpace is enabled
 *  - Uses community theme colors for backgrounds, cards, text
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Heart, ThumbsDown, Share2, MessageSquare, Send } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { getBackground, getCardBackground } from '../../utils/backgroundHelpers';
import { transformColor, type ColorTransformOptions } from '../../utils/colorTransform';
import { getThemeByKey, type PresetThemeKey, PRESET_THEMES } from '@theme/tokens';
import resolveAvatar from '../../lib/resolveAvatar';
import { postEventBus } from '../../lib/postEventBus';
import { usePostSync } from '../../hooks/usePostSync';
import { commentEventBus } from '../../lib/commentEventBus';
import { useCommentSync } from '../../hooks/useCommentSync';
import { normalizeLikeCounts } from '../shared/hooks/useFeedData';

import { fetchWithAuth } from '../../lib/fetchWithAuth';
// ─── Types ───────────────────────────────────────────────────────────────────

type Community = {
  id: string;
  name: string;
  nameSlug: string;
  communityCustomization?: any;
};

type Post = {
  id: string;
  author: string;
  authorId: string;
  authorUsername?: string;
  authorAvatar?: string;
  title?: string;
  slug?: string;
  content: string;
  mediaUrl?: string;
  imageUrl?: string;
  aboveContent?: boolean;
  tags?: string[];
  likes: number;
  dislikes: number;
  liked: boolean;
  disliked?: boolean;
  comments: number;
  shares: number;
  createdAt: string;
  community?: Community;
};

type Comment = {
  id: string;
  author: string;
  authorId: string;
  authorUsername?: string;
  authorAvatar?: string;
  content: string;
  timestamp: string;
  likes: number;
  dislikes: number;
  liked: boolean;
  disliked: boolean;
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface MobilePostViewOverlayProps {
  communitySlug: string;
  postSlug: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatTimestamp(ts: string) {
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', {
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

// ─── Build CSS custom-property style object (same pattern as MobileCommunityOverlay) ──

function buildThemeVars(raw: any): React.CSSProperties {
  if (!raw) return {};
  let c = raw;
  if (raw.baseTheme && raw.baseTheme in PRESET_THEMES) {
    const preset = getThemeByKey(raw.baseTheme as PresetThemeKey);
    c = { ...preset, ...raw };
    // Preserve preset card gradient (saved data may have stale values)
    if (preset.cardGradientEnabled) {
      c.cardGradientEnabled = true;
      if (!c.cardGradientColor1?.startsWith('#')) c.cardGradientColor1 = preset.cardGradientColor1;
      if (!c.cardGradientColor2?.startsWith('#')) c.cardGradientColor2 = preset.cardGradientColor2;
    }
  }
  if (!c.primaryColor) return {};

  const colorSpace = c.colorSpace || 'Off';

  const hexToRgbaWithOpacity = (hex: string, opacity: number) => {
    if (!hex || !hex.startsWith('#') || hex.length < 7) return hex;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
  };

  let textColor = c.textColor;
  let accentColor = c.accentColor;
  let primaryColor = c.primaryColor;
  let secondaryColor = c.secondaryColor;
  let cardBg = c.cardBg;

  if (colorSpace !== 'Off') {
    const opts: ColorTransformOptions = {
      colorSpace,
      temperatureLimit: c.temperatureLimit ?? 50,
      magentaLimit: c.magentaLimit ?? 50,
      intensityLimit: c.intensityLimit ?? 50,
      saturationLimit: c.backgroundSaturationLimit ?? 50,
      brightnessLimit: c.backgroundBrightnessLimit ?? 50,
      redLimit: c.redLimit ?? 50,
      greenLimit: c.greenLimit ?? 50,
      blueLimit: c.blueLimit ?? 50,
      contrast: c.backgroundContrast ?? 50,
      hueGravity: c.backgroundHueGravity ?? 0,
      graySaturation: c.backgroundGraySaturation ?? 0,
      targetHue: c.backgroundDefaultHue ?? 184,
    };
    textColor = transformColor(textColor, opts);
    accentColor = transformColor(accentColor, opts);
    primaryColor = transformColor(primaryColor, opts);
    secondaryColor = transformColor(secondaryColor, opts);
    if (cardBg?.startsWith('#')) cardBg = transformColor(cardBg, opts);
  }

  const navbarBg = hexToRgbaWithOpacity(
    c.navbarBg?.startsWith('#') ? (colorSpace !== 'Off' ? transformColor(c.navbarBg, { colorSpace } as any) : c.navbarBg) : primaryColor,
    c.navbarOpacity ?? 92,
  );
  const tabBarBg = c.tabBarBg
    ? hexToRgbaWithOpacity(c.tabBarBg.startsWith('#') && colorSpace !== 'Off' ? transformColor(c.tabBarBg, { colorSpace } as any) : c.tabBarBg, c.tabBarOpacity ?? 88)
    : navbarBg;

  const bg = getBackground(
    { primaryColor, secondaryColor, cardBg: cardBg || primaryColor },
    { gradientEnabled: c.gradientEnabled, gradientAngle: c.gradientAngle, gradientFavorability: c.gradientFavorability, gradientStrength: c.gradientStrength },
    primaryColor,
  );

  return {
    '--text-color': textColor,
    '--color-accent': accentColor,
    '--primary-color': primaryColor,
    '--secondary-color': secondaryColor,
    '--card-bg': getCardBackground(
      { primaryColor, secondaryColor, cardBg: cardBg || primaryColor, cardGradientColor1: c.cardGradientColor1, cardGradientColor2: c.cardGradientColor2 },
      { cardGradientEnabled: c.cardGradientEnabled, cardGradientAngle: c.cardGradientAngle, cardGradientFavorability: c.cardGradientFavorability, cardGradientStrength: c.cardGradientStrength, cardOpacity: c.cardOpacity },
      primaryColor,
    ),
    '--navbar-bg': navbarBg,
    '--tab-bar-bg': tabBarBg,
    '--app-bg': bg,
  } as React.CSSProperties;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function MobilePostViewOverlay({ communitySlug, postSlug }: MobilePostViewOverlayProps) {
  const navigate = useNavigate();
  const { user: authUser, setShowLoginModal } = useAuth();
  const { theme: siteTheme } = useTheme();

  const [post, setPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [communityCustomization, setCommunityCustomization] = useState<any>(null);
  const [communityName, setCommunityName] = useState<string>(communitySlug);
  const [themeLoading, setThemeLoading] = useState(true);

  // ── Build CSS custom properties from community customization (same as MobileCommunityOverlay) ──
  const themeVars = useMemo(() => buildThemeVars(communityCustomization), [communityCustomization]);

  // ── Always fetch community customization (community posts belong to their community's theme) ──
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/community/slug/${communitySlug}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const community = data.community;
          if (community?.name) setCommunityName(community.name);
          if (community?.communityCustomization) {
            setCommunityCustomization(community.communityCustomization);
          }
        }
      } catch {
        // ignore — will use site theme
      } finally {
        setThemeLoading(false);
      }
    })();
  }, [communitySlug]);

  // ── Fetch post + comments ──────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/community/slug/${communitySlug}/posts/${postSlug}`, { credentials: 'include' });
        if (!res.ok) { setLoading(false); return; }

        const data = await res.json();
        const p = data.post;
        if (p.author) {
          p.authorAvatar = resolveAvatar(p.author.profilePicture || p.author.image || p.authorAvatar);
        }
        setPost(normalizeLikeCounts(p));

        // Fetch replies
        if (p.community?.id && p.id) {
          const repliesRes = await fetchWithAuth(`/api/communities/${p.community.id}/posts/${p.id}/replies?limit=100`,
            { credentials: 'include' }
          );
          if (repliesRes.ok) {
            const repliesData = await repliesRes.json();
            setComments(
              (repliesData.replies || []).map((r: any) => ({
                id: r.id,
                content: r.content,
                author: r.author?.profileName || r.author?.name || r.author?.username || 'Unknown',
                authorId: r.author?.id || r.authorId,
                authorUsername: r.author?.username,
                authorAvatar: resolveAvatar(r.author?.profilePicture || r.author?.image),
                timestamp: r.createdAt,
                likes: typeof r.likes === 'number' ? r.likes : (r._count?.likes || 0),
                dislikes: typeof r.dislikes === 'number' ? r.dislikes : (r._count?.dislikes || 0),
                liked: typeof r.liked === 'boolean' ? r.liked : false,
                disliked: typeof r.disliked === 'boolean' ? r.disliked : false,
              }))
            );
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [communitySlug, postSlug]);

  // ── Sync events ───────────────────────────────────────────────────────────

  usePostSync({
    onLike: (data) => {
      if (post?.id === data.postId) setPost(prev => prev ? { ...prev, liked: data.liked, likes: data.likes } : null);
    },
    onDislike: (data) => {
      if (post?.id === data.postId) setPost(prev => prev ? { ...prev, disliked: data.disliked, dislikes: data.dislikes } : null);
    },
    postIds: post ? [post.id] : undefined,
  });

  useCommentSync({
    onLike: (data) => setComments(prev => prev.map(c =>
      c.id === data.commentId.toString() ? { ...c, liked: data.liked, likes: data.likes } : c
    )),
    onDislike: (data) => setComments(prev => prev.map(c =>
      c.id === data.commentId.toString() ? { ...c, disliked: data.disliked, dislikes: data.dislikes } : c
    )),
  });

  // ── Actions ───────────────────────────────────────────────────────────────

  const likePost = async () => {
    if (!authUser) { setShowLoginModal(true); return; }
    if (!post) return;
    const wasLiked = post.liked;
    const newLiked = !wasLiked;
    const newLikes = wasLiked ? post.likes - 1 : post.likes + 1;
    setPost(prev => prev ? { ...prev, liked: newLiked, likes: newLikes } : null);
    postEventBus.emitLike(post.id, newLiked, newLikes);
    try {
      const res = await fetchWithAuth(`/api/communities/${post.community?.id}/posts/${post.id}/like`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) {
        setPost(prev => prev ? { ...prev, liked: wasLiked, likes: post.likes } : null);
        postEventBus.emitLike(post.id, wasLiked, post.likes);
      }
    } catch {
      setPost(prev => prev ? { ...prev, liked: wasLiked, likes: post.likes } : null);
      postEventBus.emitLike(post.id, wasLiked, post.likes);
    }
  };

  const dislikePost = async () => {
    if (!authUser) { setShowLoginModal(true); return; }
    if (!post) return;
    const wasDisliked = post.disliked || false;
    const newDisliked = !wasDisliked;
    const newDislikes = wasDisliked ? post.dislikes - 1 : post.dislikes + 1;
    setPost(prev => prev ? { ...prev, disliked: newDisliked, dislikes: newDislikes } : null);
    postEventBus.emitDislike(post.id, newDisliked, newDislikes);
    try {
      const res = await fetchWithAuth(`/api/communities/${post.community?.id}/posts/${post.id}/dislike`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) {
        setPost(prev => prev ? { ...prev, disliked: wasDisliked, dislikes: post.dislikes } : null);
        postEventBus.emitDislike(post.id, wasDisliked, post.dislikes);
      }
    } catch {
      setPost(prev => prev ? { ...prev, disliked: wasDisliked, dislikes: post.dislikes } : null);
      postEventBus.emitDislike(post.id, wasDisliked, post.dislikes);
    }
  };

  const handleShare = () => {
    if (!post) return;
    const url = `${window.location.origin}/community/${communitySlug}/${postSlug}`;
    if (navigator.share) {
      navigator.share({ title: post.title || 'Post', url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url).then(() => alert('Link copied!')).catch(() => {});
    }
  };

  const handlePostComment = async () => {
    if (!authUser) { setShowLoginModal(true); return; }
    if (!newComment.trim() || !post?.community?.id) return;
    setSubmitting(true);
    try {
      const res = await fetchWithAuth(`/api/communities/${post.community.id}/posts/${post.id}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newComment.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        const reply = data.reply;
        const formatted: Comment = {
          id: reply.id,
          content: reply.content || newComment.trim(),
          author: reply.author?.profileName || reply.author?.name || reply.author?.username || authUser.displayName || authUser.username,
          authorId: reply.author?.id || authUser.id,
          authorUsername: reply.author?.username || authUser.username,
          authorAvatar: resolveAvatar(reply.author?.profilePicture || reply.author?.image || authUser.avatar),
          timestamp: reply.createdAt || new Date().toISOString(),
          likes: 0,
          dislikes: 0,
          liked: false,
          disliked: false,
        };
        setComments(prev => [formatted, ...prev]);
        setNewComment('');
        setPost(prev => prev ? { ...prev, comments: prev.comments + 1 } : null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const likeComment = async (commentId: string) => {
    if (!authUser) { setShowLoginModal(true); return; }
    if (!post?.community?.id) return;
    const c = comments.find(x => x.id === commentId);
    if (!c) return;
    const newLiked = !c.liked;
    const newLikes = c.liked ? c.likes - 1 : c.likes + 1;
    setComments(prev => prev.map(x => x.id === commentId ? { ...x, liked: newLiked, likes: newLikes } : x));
    commentEventBus.emitLike(parseInt(commentId), newLiked, newLikes);
    try {
      const res = await fetchWithAuth(`/api/communities/${post.community.id}/replys/${commentId}/like`, { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        setComments(prev => prev.map(x => x.id === commentId ? { ...x, liked: c.liked, likes: c.likes } : x));
        commentEventBus.emitLike(parseInt(commentId), c.liked, c.likes);
      }
    } catch {
      setComments(prev => prev.map(x => x.id === commentId ? { ...x, liked: c.liked, likes: c.likes } : x));
      commentEventBus.emitLike(parseInt(commentId), c.liked, c.likes);
    }
  };

  const dislikeComment = async (commentId: string) => {
    if (!authUser) { setShowLoginModal(true); return; }
    if (!post?.community?.id) return;
    const c = comments.find(x => x.id === commentId);
    if (!c) return;
    const newDisliked = !c.disliked;
    const newDislikes = c.disliked ? c.dislikes - 1 : c.dislikes + 1;
    setComments(prev => prev.map(x => x.id === commentId ? { ...x, disliked: newDisliked, dislikes: newDislikes } : x));
    commentEventBus.emitDislike(parseInt(commentId), newDisliked, newDislikes);
    try {
      const res = await fetchWithAuth(`/api/communities/${post.community.id}/replys/${commentId}/dislike`, { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        setComments(prev => prev.map(x => x.id === commentId ? { ...x, disliked: c.disliked, dislikes: c.dislikes } : x));
        commentEventBus.emitDislike(parseInt(commentId), c.disliked, c.dislikes);
      }
    } catch {
      setComments(prev => prev.map(x => x.id === commentId ? { ...x, disliked: c.disliked, dislikes: c.dislikes } : x));
      commentEventBus.emitDislike(parseInt(commentId), c.disliked, c.dislikes);
    }
  };

  // ── Extract colors from themeVars (falls back to site CSS vars / defaults) ──

  const tv = themeVars as Record<string, string>;
  const textColor = tv['--text-color'] || siteTheme.textColor || '#ffffff';
  const accentColor = tv['--color-accent'] || siteTheme.accentColor || '#06b6d4';
  const cardBg = tv['--card-bg'] || 'var(--card-bg)';
  const navBg = tv['--navbar-bg'] || siteTheme.navbarBg || siteTheme.primaryColor;

  // ── Loading state ─────────────────────────────────────────────────────────

  // Don't render overlay while loading — previous page stays visible with its own theme.
  // Once both theme + data are ready, the fully-themed overlay appears in one shot.
  if (loading || themeLoading) return null;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col overflow-hidden"
      style={{ background: 'var(--app-bg, #0a0a0a)', ...themeVars }}
    >
      {/* ── Header ── */}
      <div
        className="flex-shrink-0 flex items-center gap-3 px-4"
        style={{
          background: navBg,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          minHeight: 56,
          paddingTop: 'max(env(safe-area-inset-top, 0px), 8px)',
          paddingBottom: 8,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
        >
          <ArrowLeft size={20} style={{ color: textColor }} />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold truncate" style={{ color: textColor }}>
            {communityName} · Post
          </h2>
          {post?.title && (
            <p className="text-xs truncate" style={{ color: textColor + '99' }}>{post.title}</p>
          )}
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-4 flex flex-col gap-4" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 80px)' }}>

          {!post ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <span className="text-base font-semibold" style={{ color: textColor }}>Post not found</span>
              <span className="text-sm text-center" style={{ color: textColor + '80' }}>
                This post may have been deleted or doesn't exist.
              </span>
            </div>
          ) : (
            <>
              {/* ── Post Card ── */}
              <div
                className="rounded-2xl p-4 border"
                style={{ background: cardBg, borderColor: accentColor + '30' }}
              >
                {/* Author row */}
                <div className="flex items-center gap-3 mb-3">
                  <Link
                    to={post.authorUsername ? `/u/${post.authorUsername}` : '#'}
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)` }}
                  >
                    {post.authorAvatar ? (
                      <img src={post.authorAvatar} alt={String(post.author)} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <span className="text-sm font-bold" style={{ color: '#fff' }}>
                        {(typeof post.author === 'string' ? post.author : (post.author as any)?.username || 'U')[0]}
                      </span>
                    )}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: textColor }}>
                      {typeof post.author === 'string' ? post.author : ((post.author as any)?.profileName || (post.author as any)?.name || (post.author as any)?.username || 'Unknown')}
                    </p>
                    {post.authorUsername && (
                      <p className="text-xs" style={{ color: textColor + '88' }}>@{post.authorUsername}</p>
                    )}
                  </div>
                  <span className="text-xs flex-shrink-0" style={{ color: textColor + '66' }}>
                    {formatTimestamp(post.createdAt)}
                  </span>
                </div>

                {/* Title */}
                {post.title && (
                  <h1 className="text-lg font-bold mb-2 leading-snug" style={{ color: textColor }}>{post.title}</h1>
                )}

                {/* Media above content */}
                {(post.mediaUrl || post.imageUrl) && post.aboveContent && (
                  <img
                    src={post.mediaUrl || post.imageUrl}
                    alt="Post media"
                    className="w-full rounded-xl mb-3 object-cover max-h-96"
                  />
                )}

                {/* Content */}
                <p className="text-sm leading-relaxed mb-3" style={{ color: textColor + 'ee' }}>{post.content}</p>

                {/* Media below content */}
                {(post.mediaUrl || post.imageUrl) && !post.aboveContent && (
                  <img
                    src={post.mediaUrl || post.imageUrl}
                    alt="Post media"
                    className="w-full rounded-xl mb-3 object-cover max-h-96"
                  />
                )}

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {post.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2.5 py-0.5 rounded-full text-xs"
                        style={{ backgroundColor: accentColor + '25', color: textColor }}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Divider */}
                <div className="my-2" style={{ borderTop: `1px solid ${accentColor}20` }} />

                {/* Action row */}
                <div className="flex items-center gap-5">
                  <button
                    onClick={likePost}
                    className="flex items-center gap-1.5 text-sm"
                    style={{ color: post.liked ? '#f87171' : textColor + '88' }}
                  >
                    <Heart size={18} className={post.liked ? 'fill-current' : ''} />
                    <span>{post.likes}</span>
                  </button>
                  <button
                    onClick={dislikePost}
                    className="flex items-center gap-1.5 text-sm"
                    style={{ color: post.disliked ? '#f97316' : textColor + '88' }}
                  >
                    <ThumbsDown size={18} className={post.disliked ? 'fill-current' : ''} />
                    <span>{post.dislikes || 0}</span>
                  </button>
                  <div className="flex items-center gap-1.5 text-sm" style={{ color: textColor + '88' }}>
                    <MessageSquare size={18} />
                    <span>{post.comments}</span>
                  </div>
                  <button
                    onClick={handleShare}
                    className="ml-auto flex items-center gap-1.5 text-sm"
                    style={{ color: textColor + '88' }}
                  >
                    <Share2 size={18} />
                  </button>
                </div>
              </div>

              {/* ── Comment Input ── */}
              <div
                className="rounded-2xl p-3 border"
                style={{ background: cardBg, borderColor: accentColor + '30' }}
              >
                <div className="flex gap-2 items-start">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)` }}
                  >
                    {authUser?.avatar || (authUser as any)?.profilePicture ? (
                      <img
                        src={resolveAvatar(authUser?.avatar || (authUser as any)?.profilePicture)}
                        alt="You"
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-xs font-bold" style={{ color: '#fff' }}>
                        {authUser?.displayName?.[0] || authUser?.username?.[0] || 'U'}
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <textarea
                      value={newComment}
                      onChange={e => setNewComment(e.target.value)}
                      placeholder="Write a reply…"
                      rows={2}
                      className="w-full bg-transparent resize-none outline-none"
                      style={{ color: textColor, fontSize: '16px' }}
                    />
                  </div>
                  <button
                    onClick={handlePostComment}
                    disabled={submitting || !newComment.trim()}
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 disabled:opacity-40"
                    style={{ backgroundColor: accentColor }}
                  >
                    <Send size={14} style={{ color: '#fff' }} />
                  </button>
                </div>
              </div>

              {/* ── Replies List ── */}
              {comments.length > 0 && (
                <div className="flex flex-col gap-3">
                  <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: textColor + '77' }}>
                    Replies
                  </p>
                  {comments.map(comment => (
                    <div
                      key={comment.id}
                      className="rounded-2xl p-3 border"
                      style={{ background: cardBg, borderColor: accentColor + '20' }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Link
                          to={comment.authorUsername ? `/u/${comment.authorUsername}` : '#'}
                          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ background: `linear-gradient(135deg, ${accentColor}88, ${accentColor}55)` }}
                        >
                          {comment.authorAvatar ? (
                            <img src={comment.authorAvatar} alt={comment.author} className="w-full h-full rounded-full object-cover" />
                          ) : (
                            <span className="text-xs font-bold" style={{ color: '#fff' }}>{comment.author?.[0] || 'U'}</span>
                          )}
                        </Link>
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-semibold truncate block" style={{ color: textColor }}>
                            {comment.author}
                          </span>
                        </div>
                        <span className="text-xs flex-shrink-0" style={{ color: textColor + '55' }}>
                          {formatTimestamp(comment.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm mb-2" style={{ color: textColor + 'dd' }}>{comment.content}</p>
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => likeComment(comment.id)}
                          className="flex items-center gap-1 text-xs"
                          style={{ color: comment.liked ? '#f87171' : textColor + '66' }}
                        >
                          <Heart size={13} className={comment.liked ? 'fill-current' : ''} />
                          <span>{comment.likes}</span>
                        </button>
                        <button
                          onClick={() => dislikeComment(comment.id)}
                          className="flex items-center gap-1 text-xs"
                          style={{ color: comment.disliked ? '#f97316' : textColor + '66' }}
                        >
                          <ThumbsDown size={13} className={comment.disliked ? 'fill-current' : ''} />
                          <span>{comment.dislikes}</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {comments.length === 0 && !loading && (
                <div className="flex flex-col items-center py-8 gap-2">
                  <MessageSquare size={28} style={{ color: textColor + '44' }} />
                  <span className="text-sm" style={{ color: textColor + '66' }}>No replies yet. Be the first!</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
